import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate, useSearchParams } from "react-router-dom";

import { RepeatMDFooterV3 } from "@/atoms/repeatmd-footer-v3/RepeatMDFooterV3";
import { PlatformLogo } from "@/components/PlatformLogo";
import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { AnonymousLogin } from "@/modules/onboarding/components/AnonymousLogin/AnonymousLogin";
import { ExitSignUpLink } from "@/modules/onboarding/components/ExitSignUpLink/ExitSignUpLink";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import SignUpPerks from "@/modules/onboarding/components/SignUpPerks/SignUpPerks";
import { useOnboardingContext } from "@/modules/onboarding/context";
import { LoginOrSignUpForm } from "@/modules/onboarding/forms/LoginOrSignUpForm/LoginOrSignUpForm";
import { storage } from "@/modules/storage";
import { queryClient } from "@/pages/Root";
import Services from "@/services/Services";
import { waitObj } from "@/toolbox/Promise";
import {
  getMultiPlatformPlatformByPlatformIdTheme,
  getPlatformCurrent,
  getPlatformTheme,
  postAuthExists,
  postAuthLogin,
  postMultiPlatformAuthExists,
} from "api/methods";
import { isNativeMobileApp } from "utils/platform.helpers";

type LoginResponse = {
  success: boolean;
  message?: string;
};

const load = async ({ url }: Request) => {
  const urlObj = new URL(url);
  let referralCode = urlObj.searchParams.get("referralCode");
  const checkInCode = urlObj.searchParams.get("checkInCode");

  if (!referralCode) {
    referralCode = storage.getItem("referralCode");
  }

  let statelessReferralCode = urlObj.searchParams.get("statelessReferralCode");

  if (!statelessReferralCode) {
    statelessReferralCode = storage.getItem("statelessReferralCode");
  }

  const localStoragePlatformId = storage.getItem("platformId");

  // Which endpoint identifies the practice depends on the surface, not on what is stored:
  // the web app is served from a per-practice host, so the host-scoped theme is always
  // right there, while the native app has no such host and must look the practice up by
  // the platform id it stored. Same split as the root loader in pages/Root.
  const theme = isNativeMobileApp() ? null : queryClient.fetchQuery(getPlatformTheme.keyed());

  const multiPlatformTheme =
    isNativeMobileApp() && localStoragePlatformId
      ? queryClient.fetchQuery(
          getMultiPlatformPlatformByPlatformIdTheme.keyed({ platformId: localStoragePlatformId }),
        )
      : null;

  const isAuthorized = Services.auth.isAuthorized();
  const platform = queryClient.fetchQuery({
    ...getPlatformCurrent.keyed(),
    queryFn: () => getPlatformCurrent().catch(() => null),
  });

  return waitObj({
    referralCode,
    statelessReferralCode,
    theme,
    multiPlatformTheme,
    isAuthorized,
    platform,
    checkInCode,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const v3SignInDisplayLoader: LoaderFunction = ({ request }) => {
  return load(request);
};

const clearGiftCardData = () => {
  storage.removeItem("isGiftCardClaim");
  storage.removeItem("giftCardTransferId");
  storage.removeItem("giftCardRecipientPhone");
  storage.removeItem("giftCardRecipientPlatformUserId");
  storage.removeItem("giftCardImageUrl");
  storage.removeItem("giftCardAmount");
  storage.removeItem("referralSenderName");
  storage.removeItem("giftCardInfo");
};

const V3SignInPage = () => {
  const {
    theme,
    multiPlatformTheme,
    platform: loadedPlatform,
    isAuthorized,
    referralCode: loadedReferralCode,
    statelessReferralCode: loadedStatelessReferralCode,
    checkInCode: loadedCheckInCode,
  } = (useLoaderData() as LoaderData) || {};
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    flow,
    platformSelection,
    updateFlow,
    updateReferral,
    updatePlatformSelection,
    setPlatformId,
    updateAuth,
  } = useOnboardingContext();

  const [searchParams] = useSearchParams();
  // Bumped by "No thanks" to remount the sign-up form and clear what the user typed.
  const [signUpResetCount, setSignUpResetCount] = useState(0);
  const comingFromDeepLink = !!platformSelection.platformId || !!storage.getItem("platformId");

  useEffect(() => {
    // Read from URL params first, then fall back to loader data (which reads from storage).
    // This ensures referral codes from deep links (AppsFlyer or direct) are captured
    // regardless of how they were stored.
    const checkInCode = searchParams.get("checkInCode") || loadedCheckInCode;
    const referralCode = searchParams.get("referralCode") || loadedReferralCode || undefined;
    const platformId = searchParams.get("platformId") || loadedPlatform?.id;
    const locationId = searchParams.get("locationId");
    let derivedLocationId: string | undefined;
    const statelessReferralCode =
      searchParams.get("statelessReferralCode") || loadedStatelessReferralCode || undefined;

    if (referralCode && referralCode !== storage.getItem("referralCode")) {
      updateReferral({ referralCode });
      storage.setItem("referralCode", referralCode);
    }

    if (statelessReferralCode) {
      updateReferral({ statelessReferralCode });
      storage.setItem("statelessReferralCode", statelessReferralCode);
    }

    if (checkInCode) {
      // If checkInCode is a full URL for a visit check-in, extract the last
      // path segment (the ID) and treat it as a locationId fallback.
      try {
        const checkInUrl = new URL(checkInCode);
        if (checkInUrl.pathname.startsWith("/api/v1/visit/checkin/")) {
          const segments = checkInUrl.pathname.split("/").filter(Boolean);
          derivedLocationId = segments.at(-1);
        }
      } catch {
        // Not a valid URL, ignore and just store the raw checkInCode
      }

      updateReferral({ checkInCode });
    }

    if (platformId) {
      setPlatformId(platformId);
    }

    const finalLocationId = locationId ?? derivedLocationId;
    if (finalLocationId) {
      updatePlatformSelection({ locationId: finalLocationId });
    }
  }, [
    searchParams,
    updateReferral,
    setPlatformId,
    updatePlatformSelection,
    loadedReferralCode,
    loadedStatelessReferralCode,
    loadedCheckInCode,
    loadedPlatform?.id,
  ]);

  // Check if coming from gift card claim flow
  const isGiftCardClaim = storage.getItem("isGiftCardClaim") === "true";
  const giftCardRecipientPhone = storage.getItem("giftCardRecipientPhone") || "";
  // Format phone number for display (remove +1 and format as (XXX) XXX-XXXX)
  const initialPhone =
    isGiftCardClaim && giftCardRecipientPhone ? giftCardRecipientPhone.replace("+1", "") : "";

  const proceedToVerification = (normalizedPhone: string) => {
    updateAuth({ phoneNumber: normalizedPhone });
    navigate("/signin/verification");
  };

  const sendLogin = useCallback(
    async (normalizedPhone: string) => {
      const loginResponse = (await postAuthLogin({
        phoneNumber: normalizedPhone,
      })) as LoginResponse;
      if (!loginResponse.success) {
        throw new Error(loginResponse.message || t("v3SignInPage.unableToSendVerificationCode"));
      }
    },
    [t],
  );

  const onPhoneNumberSubmitted = useCallback(
    async (formattedPhoneNumber: string) => {
      updateFlow({ isNewUser: false });
      const normalizedPhone = "+1" + formattedPhoneNumber.replace(/[^\d]/g, "");

      // If user changed the phone number, clear gift card data and proceed with normal registration
      if (isGiftCardClaim && giftCardRecipientPhone && normalizedPhone !== giftCardRecipientPhone) {
        clearGiftCardData();
      }

      const existsFn = isNativeMobileApp() ? postMultiPlatformAuthExists : postAuthExists;

      let userExistsResponse;
      try {
        userExistsResponse = await existsFn({
          phoneNumber: normalizedPhone,
          ...(platformSelection.platformId && {
            platformId: platformSelection.platformId,
          }),
        });
      } catch (err) {
        console.error("User existence check failed:", err);
        // If existence check fails (network/404), assume new user and show consent step
        updateFlow({ isNewUser: true });
        return;
      }

      if (
        userExistsResponse?.exists &&
        !userExistsResponse.isRegistrationPending &&
        userExistsResponse.areTosAccepted
      ) {
        await sendLogin(normalizedPhone);
        proceedToVerification(normalizedPhone);
      } else {
        // Treat as new user: require SMS agreement before sending code
        updateFlow({ isNewUser: true });
      }
    },
    [
      sendLogin,
      proceedToVerification,
      isGiftCardClaim,
      giftCardRecipientPhone,
      updateFlow,
      platformSelection.platformId,
    ],
  );

  const handleSendVerificationForNewUser = useCallback(
    async (formattedPhoneNumber: string) => {
      const normalizedPhone = "+1" + formattedPhoneNumber.replaceAll(/[^\d]/g, "");

      await sendLogin(normalizedPhone);
      proceedToVerification(normalizedPhone);
    },
    [sendLogin, proceedToVerification],
  );

  // "No thanks": abandon sign-up and hand back a clean screen — out of the marketing step,
  // with the number and consent checkbox cleared (the bump remounts the form below).
  const handleExitSignUp = useCallback(() => {
    updateFlow({ isNewUser: false });
    setSignUpResetCount((count) => count + 1);
  }, [updateFlow]);

  const onSubmitHandler = flow.isNewUser
    ? handleSendVerificationForNewUser
    : onPhoneNumberSubmitted;

  const logoUrl = isNativeMobileApp()
    ? comingFromDeepLink
      ? multiPlatformTheme?.logoImageUrl
      : null
    : theme?.platformLogoUrl;

  return (
    <MobileViewportContainer contentClassName="pt-0 flex flex-col justify-between min-h-[calc(100vh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] desk:min-h-0 desk:gap-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex-1"
      >
        <LoginOrSignUpForm
          // Remounting on exit is what clears the form: the phone value and the consent
          // checkbox are the form's own state, so a new key drops both. After a reset the
          // field starts empty even if a gift-card claim seeded it originally.
          key={signUpResetCount}
          initialPhoneNumber={signUpResetCount === 0 ? initialPhone : ""}
          onSubmit={onSubmitHandler}
          consentRequired={flow.isNewUser}
          practiceName={loadedPlatform?.name}
          submitLabel={flow.isNewUser ? t("v3SignInPage.unlockRewards") : undefined}
          prefix={<PlatformLogo logoUrl={logoUrl} className="mb-14" />}
          // Twilio compliance: the "No thanks" exit is available in both states, while
          // marketing content may not share the page with the transactional opt-in
          // disclosure — so the perks appear only on the marketing step.
          suffix={
            <>
              <ExitSignUpLink
                websiteUrl={loadedPlatform?.website}
                bookAppointmentUrl={loadedPlatform?.bookAppointmentUrl}
                onReset={handleExitSignUp}
              />
              {flow.isNewUser ? (
                <SignUpPerks
                  rewardsEnabled={loadedPlatform?.platformSettings?.rewardsEnabled ?? true}
                  spendBasedRewardsEnabled={
                    loadedPlatform?.platformSettings?.spendBasedRewardsEnabled ?? true
                  }
                  birthdayOfferEnabled={
                    loadedPlatform?.platformSettings?.hasActiveBirthdayOffer ?? true
                  }
                  className="mt-8"
                  animate
                />
              ) : null}
            </>
          }
        />
      </motion.div>
      <div className="mt-auto">
        <AnonymousLogin isAuthorized={!!isAuthorized} xsText className="mx-auto" />
        <RepeatMDFooterV3 showLinks />
      </div>
    </MobileViewportContainer>
  );
};

export default V3SignInPage;
